import * as React from "react";
import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import {
    AlertCircle,
    ArrowRight,
    ChevronRight,
    Phone,
    QrCode,
    Search,
    Share2,
    ShoppingBag,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { MetaFunction } from "react-router";

import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useClientHttpActions } from "@/lib/utils/http/http-client.actions";
import { IBusiness } from "@/types/business.type";

export const meta: MetaFunction = () => {
    return [
        { title: "Find a Merchant | Kwik Nkap" },
        {
            name: "description",
            content: "Search for a merchant and pay quickly and securely with Kwik Nkap.",
        },
    ];
};

// Canonical Green Ledger entrance easing ("expo-out"), matching the landing site.
const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

// Shared editorial container + section rhythm (mirrors the landing page).
const CONTAINER = "mx-auto w-full max-w-[74rem] px-5 sm:px-6 lg:px-10";
const SECTION = "py-[clamp(64px,9vw,112px)]";

// A small square teal "ledger" bullet used to mark section starts (no copy added).
function SectionMark() {
    return <div className="mx-auto mb-6 h-[7px] w-[7px] rounded-[2px] bg-primary" />;
}

// Inline Kwik Nkap wordmark — no remote asset (the CDN PNG can be blocked
// browser-side). The mark renders in the brand teal, the letters in ink, and both
// adapt to dark mode via the theme tokens.
function KwikNkapLogo({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 228 45"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Kwik Nkap"
        >
            <g className="fill-primary">
                <path d="M15.1439 25.0705L26.9521 8.85364L26.3399 19.1253L14.2904 34.9596L15.1439 25.0705Z" />
                <path d="M13.7374 34.8531L4.06608 33.4757C1.5879 33.1284 -0.189792 30.9857 0.0162323 28.6077L2.0235 0L16.4805 3.27281L13.7433 34.859L13.7374 34.8531Z" />
                <path d="M27.6694 5.16962L37.3761 6.30568C39.8602 6.59411 41.6967 8.68965 41.5496 11.0736L40.2605 39.7225L25.7269 36.8146L27.6694 5.16962Z" />
            </g>
            <g className="fill-foreground">
                <path d="M54.0586 36.485V10.1201H59.1621V36.485H54.0586ZM69.999 36.485L58.5617 22.7227L69.6988 10.1201H76.0737L63.6946 23.847V21.4454L76.4446 36.485H69.9931H69.999Z" />
                <path d="M82.1849 36.4826L75.4332 18.3703H80.3837L84.769 31.5321L83.3033 31.5675L87.8417 18.3644H91.3677L95.9414 31.5675L94.4757 31.5321L98.867 18.3703H103.817L97.0657 36.4826H93.5045L88.966 23.9917H90.3552L85.7403 36.4826H82.179H82.1849Z" />
                <path d="M108.402 15.4404C107.601 15.4404 106.942 15.1696 106.412 14.634C105.888 14.0983 105.623 13.4273 105.623 12.6267C105.623 11.8262 105.888 11.1905 106.412 10.6371C106.936 10.0897 107.601 9.81305 108.402 9.81305C109.202 9.81305 109.897 10.0897 110.409 10.6371C110.921 11.1846 111.18 11.8497 111.18 12.6267C111.18 13.4037 110.921 14.0983 110.409 14.634C109.897 15.1696 109.226 15.4404 108.402 15.4404ZM105.888 36.4782V18.3659H110.874V36.4782H105.888Z" />
                <path d="M115.226 36.4824V9.36395H120.141V36.4765H115.226V36.4824ZM126.74 36.4824L119.765 27.1466L126.705 18.3701H132.403L124.227 28.2709L124.415 25.8693L132.738 36.4824H126.74Z" />
                <path d="M135.102 36.4826V9.77625H140.353L142.602 15.4036V36.4826H135.102ZM153.48 36.4826L137.992 17.0165L140.353 9.77625L155.84 29.2424L153.48 36.4826ZM153.48 36.4826L151.602 30.5551V9.77625H159.101V36.4767H153.474L153.48 36.4826Z" />
                <path d="M163.082 36.4824V9.02844H170.434V36.4824H163.082ZM174.973 36.4824L169.834 26.7699L174.931 18.0287H182.843L176.244 27.8942L176.432 25.4926L183.219 36.4824H174.967H174.973Z" />
                <path d="M191.843 36.8546C190.118 36.8546 188.594 36.4426 187.269 35.6185C185.945 34.7944 184.909 33.6642 184.155 32.2221C183.408 30.7858 183.031 29.1259 183.031 27.254C183.031 25.3822 183.408 23.7163 184.155 22.2683C184.903 20.8202 185.945 19.6842 187.269 18.8719C188.594 18.0595 190.118 17.6534 191.843 17.6534C192.92 17.6534 193.909 17.8359 194.822 18.1949C195.734 18.5599 196.499 19.0661 197.111 19.7136C197.724 20.367 198.118 21.1146 198.295 21.9622V32.54C198.118 33.3876 197.724 34.141 197.111 34.7885C196.499 35.4419 195.734 35.9423 194.822 36.3072C193.909 36.6722 192.914 36.8488 191.843 36.8488V36.8546ZM193.491 30.4032C194.415 30.4032 195.163 30.1089 195.722 29.5203C196.287 28.9316 196.564 28.1782 196.564 27.254C196.564 26.6301 196.44 26.0826 196.187 25.6235C195.94 25.1585 195.581 24.7935 195.116 24.5169C194.651 24.2402 194.121 24.1048 193.521 24.1048C192.92 24.1048 192.39 24.2402 191.925 24.5169C191.46 24.7935 191.095 25.1702 190.819 25.6412C190.542 26.1179 190.407 26.6536 190.407 27.254C190.407 27.8544 190.536 28.3901 190.801 28.8669C191.066 29.3437 191.431 29.7145 191.908 29.9912C192.385 30.2678 192.908 30.4032 193.479 30.4032H193.491ZM196.34 36.4779V31.5275L197.353 27.0244L196.34 22.5626V18.0242H203.504V36.4779H196.34Z" />
                <path d="M207.03 44.3601V18.0305H214.382V22.5689L213.181 27.0307L214.194 31.5338V44.3601H207.03ZM218.391 36.8609C217.29 36.8609 216.254 36.6784 215.277 36.3194C214.3 35.9544 213.475 35.4541 212.804 34.8007C212.127 34.1532 211.692 33.3997 211.492 32.5521V22.3511C211.692 21.5034 212.127 20.7205 212.804 20.0083C213.481 19.296 214.305 18.7251 215.277 18.3013C216.254 17.8774 217.29 17.6655 218.391 17.6655C220.215 17.6655 221.811 18.0717 223.17 18.884C224.53 19.6963 225.596 20.8265 226.361 22.2804C227.12 23.7285 227.503 25.3943 227.503 27.2662C227.503 29.138 227.12 30.798 226.361 32.2342C225.596 33.6705 224.536 34.8007 223.17 35.6307C221.805 36.4547 220.215 36.8668 218.391 36.8668V36.8609ZM217.001 30.4095C217.602 30.4095 218.138 30.2741 218.614 29.9974C219.091 29.7208 219.456 29.3499 219.721 28.8731C219.986 28.3963 220.115 27.8607 220.115 27.2603C220.115 26.6599 219.986 26.0889 219.721 25.6298C219.456 25.1647 219.097 24.7998 218.632 24.5231C218.167 24.2465 217.637 24.1111 217.037 24.1111C216.436 24.1111 215.907 24.2406 215.442 24.5055C214.976 24.7703 214.617 25.1353 214.353 25.6121C214.088 26.0889 213.958 26.6363 213.958 27.2603C213.958 27.8842 214.082 28.3963 214.335 28.8731C214.582 29.3499 214.941 29.7208 215.406 29.9974C215.871 30.2741 216.401 30.4095 217.001 30.4095Z" />
            </g>
        </svg>
    );
}

export default function MerchantIndexPage() {
    const [searchQuery, setSearchQuery] = useState("");
    const { getPublicBusinessInfoByUid } = useClientHttpActions();
    const { t } = useTranslation();
    const prefersReduced = useReducedMotion();

    const searchMerchant = useMutation({
        mutationFn: getPublicBusinessInfoByUid,
    });

    const handleSearch = () => {
        if (searchQuery.trim()) {
            searchMerchant.mutate(searchQuery);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && searchQuery.trim()) {
            handleSearch();
        }
    };

    // One-shot entrance reveal — no infinite/ambient loops (those caused the page
    // to never settle and repaint continuously). Distance/delay collapse to 0 for
    // users who prefer reduced motion.
    const reveal = (delay = 0) => ({
        initial: { opacity: 0, y: prefersReduced ? 0 : 16 },
        animate: { opacity: 1, y: 0 },
        transition: {
            duration: prefersReduced ? 0 : 0.6,
            delay: prefersReduced ? 0 : delay,
            ease: EASE,
        },
    });

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Hero — warm "paper" with a soft teal radial, an editorial hairline
                base, and a clean search card (no dark monolith, no infinite SVGs). */}
            <section
                className="relative overflow-hidden border-b border-border bg-background"
                style={{
                    backgroundImage: [
                        "radial-gradient(ellipse 90% 60% at 50% -10%, hsl(174 84% 38% / 0.2) 0%, transparent 60%)",
                        "radial-gradient(ellipse 72% 55% at 88% 14%, hsl(32 88% 50% / 0.13) 0%, transparent 55%)",
                        "radial-gradient(ellipse 65% 55% at 8% 92%, hsl(174 84% 42% / 0.08) 0%, transparent 55%)",
                    ].join(", "),
                }}
            >
                <div className={cn(CONTAINER, "py-[clamp(72px,10vw,124px)]")}>
                    <div className="mx-auto max-w-3xl text-center">
                        <motion.div {...reveal(0)} className="mb-7 flex justify-center">
                            <KwikNkapLogo className="h-10 w-auto" />
                        </motion.div>
                        <motion.p
                            {...reveal(0.05)}
                            className="eyebrow-accent mb-4 flex items-center justify-center gap-2"
                        >
                            <span className="inline-block h-[7px] w-[7px] rounded-[2px] bg-primary" />
                            {t("merchant_page.eyebrow")}
                        </motion.p>
                        <motion.h1
                            {...reveal(0.08)}
                            className="text-balance text-4xl font-semibold tracking-tight text-foreground md:text-5xl"
                        >
                            {t("merchant_page.search_title")}
                        </motion.h1>
                        <motion.p
                            {...reveal(0.14)}
                            className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground"
                        >
                            {t("merchant_page.subtitle")}
                        </motion.p>

                        {/* Search Box */}
                        <motion.div {...reveal(0.2)} className="mx-auto mt-10 max-w-2xl">
                            <Card className="border-border shadow-sm">
                                <CardContent className="p-3">
                                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                                        <Input
                                            placeholder={t("merchant_page.search_placeholder")}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            className="h-14 flex-1 rounded-full border-input bg-background text-base"
                                        />
                                        <Button
                                            onClick={handleSearch}
                                            disabled={searchMerchant.isPending}
                                            size="lg"
                                            className="h-12 px-8 md:h-14"
                                        >
                                            <Search className="mr-2 h-5 w-5" />
                                            {t("merchant_page.search_button")}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Search Results */}
                        <div className="mx-auto mt-6 space-y-4">
                            {searchMerchant.isPending && <MerchantCardSkeleton />}
                            {searchMerchant.isError && (
                                <motion.div {...reveal(0)}>
                                    <Card className="border-destructive/30 bg-destructive/10">
                                        <CardContent className="flex items-center gap-4 p-6 text-left text-destructive">
                                            <AlertCircle className="h-6 w-6 shrink-0" />
                                            <p>
                                                {t("merchant_page.error.merchant_not_found", {
                                                    searchQuery,
                                                })}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            )}
                            {searchMerchant.isSuccess && searchMerchant.data && (
                                <motion.div {...reveal(0)}>
                                    <MerchantCard merchant={searchMerchant.data} />
                                </motion.div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className={cn("border-b border-border bg-card", SECTION)}>
                <div className={CONTAINER}>
                    <div className="mx-auto max-w-5xl">
                        <SectionMark />
                        <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-foreground">
                            {t("merchant_page.how_it_works_title")}
                        </h2>
                        <div className="grid gap-6 md:grid-cols-3">
                            <FeatureCard
                                index="01"
                                icon={<QrCode className="h-6 w-6" />}
                                title={t("merchant_page.how_it_works.scan_qr_code")}
                                description={t(
                                    "merchant_page.how_it_works.scan_qr_code_description"
                                )}
                                prefersReduced={prefersReduced}
                            />
                            <FeatureCard
                                index="02"
                                icon={<Share2 className="h-6 w-6" />}
                                title={t("merchant_page.how_it_works.pay_merchant")}
                                description={t(
                                    "merchant_page.how_it_works.pay_merchant_description"
                                )}
                                prefersReduced={prefersReduced}
                            />
                            <FeatureCard
                                index="03"
                                icon={<ShoppingBag className="h-6 w-6" />}
                                title={t("merchant_page.how_it_works.complete_payment")}
                                description={t(
                                    "merchant_page.how_it_works.complete_payment_description"
                                )}
                                prefersReduced={prefersReduced}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* For Merchants */}
            <section className={cn("border-b border-border bg-background", SECTION)}>
                <div className={CONTAINER}>
                    <div className="mx-auto max-w-6xl">
                        <div className="grid items-center gap-12 md:grid-cols-2">
                            <motion.div
                                initial={{ opacity: 0, y: prefersReduced ? 0 : 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "0px 0px -40px 0px" }}
                                transition={{ duration: prefersReduced ? 0 : 0.6, ease: EASE }}
                            >
                                <h2 className="mb-6 text-balance text-4xl font-semibold tracking-tight text-foreground">
                                    {t("merchant_page.for_merchants.title")}
                                </h2>
                                <p className="mb-8 text-lg text-muted-foreground">
                                    {t("merchant_page.for_merchants.description")}
                                </p>
                                <div className="flex flex-col gap-4 sm:flex-row">
                                    <a
                                        href="https://app.kwiknkap.com/api/base/create-account"
                                        target="_blank"
                                        rel="noreferrer"
                                        className={cn(
                                            buttonVariants({ variant: "default", size: "lg" }),
                                            "group"
                                        )}
                                    >
                                        {t("merchant_page.for_merchants.sign_up_button")}
                                        <ChevronRight className="ml-1.5 h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                                    </a>
                                    <Button variant="outline" size="lg">
                                        {t("merchant_page.for_merchants.learn_more_button")}
                                    </Button>
                                </div>
                            </motion.div>
                            <motion.div
                                className="relative"
                                initial={{ opacity: 0, y: prefersReduced ? 0 : 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "0px 0px -40px 0px" }}
                                transition={{
                                    duration: prefersReduced ? 0 : 0.6,
                                    delay: 0.1,
                                    ease: EASE,
                                }}
                            >
                                <div className="absolute inset-0 rotate-6 rounded-3xl bg-gradient-to-br from-primary/15 to-honey/15 blur-xl" />
                                <div className="relative rounded-[24px] border border-border bg-card p-8 shadow-sm">
                                    <div className="flex aspect-square items-center justify-center">
                                        <div className="text-center">
                                            <QrCode className="mx-auto mb-6 h-44 w-44 text-primary" />
                                            <p className="text-lg font-medium text-foreground">
                                                {t(
                                                    "merchant_page.for_merchants.sample_merchant_qr"
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className={cn("border-b border-border surface-cream", SECTION)}>
                <div className={CONTAINER}>
                    <div className="mx-auto max-w-5xl">
                        <SectionMark />
                        <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-foreground">
                            {t("merchant_page.faq.title")}
                        </h2>
                        <div className="grid gap-6 md:grid-cols-2">
                            <FaqCard
                                question={t("merchant_page.faq.question")}
                                answer={t("merchant_page.faq.answer")}
                                prefersReduced={prefersReduced}
                            />
                            <FaqCard
                                question={t("merchant_page.faq2.question")}
                                answer={t("merchant_page.faq2.answer")}
                                prefersReduced={prefersReduced}
                            />
                            <FaqCard
                                question={t("merchant_page.faq3.question")}
                                answer={t("merchant_page.faq3.answer")}
                                prefersReduced={prefersReduced}
                            />
                            <FaqCard
                                question={t("merchant_page.faq4.question")}
                                answer={t("merchant_page.faq4.answer")}
                                prefersReduced={prefersReduced}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-background py-12">
                <div className={CONTAINER}>
                    <div className="mx-auto max-w-5xl text-center">
                        <div className="mb-6 space-x-6">
                            <a
                                href="https://www.kwiknkap.com/terms"
                                className="text-muted-foreground transition-colors hover:text-foreground"
                            >
                                {t("merchant_page.footer.terms")}
                            </a>
                            <a
                                href="https://www.kwiknkap.com/privacy-policy"
                                className="text-muted-foreground transition-colors hover:text-foreground"
                            >
                                {t("merchant_page.footer.privacy")}
                            </a>
                        </div>
                        <p className="eyebrow-mono">
                            {t("merchant_page.copyright", {
                                year: new Date().getFullYear(),
                            })}
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

// Merchant Card Component (search result) — clean paper card.
function MerchantCard({ merchant }: { merchant: IBusiness }) {
    const { t } = useTranslation();
    const logo = merchant.businessPhotoUrl ?? merchant.businessPhoto;
    return (
        <Card className="card-hover overflow-hidden border-border text-left shadow-sm">
            <CardContent className="p-6">
                <div className="flex flex-col items-center gap-6 sm:flex-row">
                    <div className="relative h-20 w-20 flex-shrink-0">
                        {logo ? (
                            <img
                                src={logo}
                                alt={merchant.businessName}
                                width={80}
                                height={80}
                                className="h-20 w-20 rounded-sm object-cover"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center rounded-sm bg-accent">
                                <ShoppingBag className="h-10 w-10 text-primary" />
                            </div>
                        )}
                    </div>

                    <div className="min-w-0 flex-1 text-center sm:text-left">
                        <h3 className="mb-2 text-2xl font-semibold text-foreground">
                            {merchant.businessName}
                        </h3>
                        <div className="space-y-1">
                            <p className="flex items-center justify-center gap-2 text-muted-foreground sm:justify-start">
                                <QrCode className="h-4 w-4" />
                                <span className="num-display text-sm">{merchant.businessUid}</span>
                            </p>
                            {merchant.phoneNumber && (
                                <p className="flex items-center justify-center gap-2 text-muted-foreground sm:justify-start">
                                    <Phone className="h-4 w-4" />
                                    <span className="num-display text-sm">
                                        {merchant.phoneNumber}
                                    </span>
                                </p>
                            )}
                        </div>
                    </div>

                    <Button asChild size="lg" className="w-full sm:w-auto">
                        <Link to={`/merchant/${merchant.businessUid}`}>
                            {t("pay_now")}
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

// Feature Card — teal-tint icon chip + mono ledger index, one-shot reveal on scroll.
function FeatureCard({
    index,
    icon,
    title,
    description,
    prefersReduced,
}: {
    index: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    prefersReduced: boolean | null;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: prefersReduced ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px -40px 0px" }}
            transition={{ duration: prefersReduced ? 0 : 0.6, ease: EASE }}
        >
            <Card className="card-hover relative h-full border-border bg-card shadow-sm">
                <CardContent className="p-8">
                    <span className="num-display absolute right-6 top-6 text-2xl text-muted-foreground/40">
                        {index}
                    </span>
                    <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-primary">
                        {icon}
                    </div>
                    <h3 className="mb-3 text-xl font-semibold text-foreground">{title}</h3>
                    <p className="text-muted-foreground">{description}</p>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// FAQ Card.
function FaqCard({
    question,
    answer,
    prefersReduced,
}: {
    question: string;
    answer: string;
    prefersReduced: boolean | null;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: prefersReduced ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px -40px 0px" }}
            transition={{ duration: prefersReduced ? 0 : 0.6, ease: EASE }}
        >
            <Card className="card-hover h-full border-border bg-card shadow-sm">
                <CardContent className="p-8">
                    <h3 className="mb-3 text-xl font-semibold text-foreground">{question}</h3>
                    <p className="text-muted-foreground">{answer}</p>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// Loading Skeleton (search result) — warm paper tones.
function MerchantCardSkeleton() {
    return (
        <Card className="overflow-hidden border-border shadow-sm">
            <CardContent className="p-6">
                <div className="flex items-center gap-6">
                    <Skeleton className="h-20 w-20 rounded-sm" />
                    <div className="flex-1">
                        <Skeleton className="mb-4 h-8 w-64" />
                        <Skeleton className="mb-2 h-5 w-48" />
                        <Skeleton className="h-5 w-40" />
                    </div>
                    <Skeleton className="hidden h-12 w-32 rounded-full sm:block" />
                </div>
            </CardContent>
        </Card>
    );
}

// Pending UI shown while the route hydrates — warm paper, matches the page.
export function HydrateFallback() {
    return (
        <div className="min-h-screen animate-pulse bg-background">
            <div className="mx-auto max-w-4xl px-4 py-16">
                {/* Header skeleton */}
                <header className="mb-16 text-center">
                    <div className="mb-6 flex justify-center">
                        <Skeleton className="h-16 w-16 rounded-full" />
                    </div>
                    <Skeleton className="mx-auto mb-4 h-12 w-3/4" />
                    <Skeleton className="mx-auto h-6 w-1/2" />
                </header>

                <main>
                    {/* Search skeleton */}
                    <Card className="mb-16 border-border shadow-sm">
                        <CardContent className="pb-8 pt-8">
                            <Skeleton className="mx-auto mb-6 h-8 w-3/4" />
                            <div className="mx-auto flex max-w-xl flex-col gap-4 md:flex-row">
                                <Skeleton className="h-12 w-full md:w-3/4" />
                                <Skeleton className="h-12 w-full rounded-full md:w-1/4" />
                            </div>
                        </CardContent>
                    </Card>

                    {/* How it works skeleton */}
                    <div className="mb-20">
                        <Skeleton className="mx-auto mb-12 h-10 w-1/2" />
                        <div className="grid gap-8 md:grid-cols-3">
                            {[...Array(3)].map((_, index) => (
                                <Card key={index} className="border-border shadow-sm">
                                    <CardContent className="pb-8 pt-8 text-center">
                                        <Skeleton className="mx-auto mb-6 h-16 w-16 rounded-full" />
                                        <Skeleton className="mx-auto mb-3 h-6 w-3/4" />
                                        <Skeleton className="mx-auto mb-2 h-4 w-full" />
                                        <Skeleton className="mx-auto h-4 w-full" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>

                    {/* For merchants skeleton */}
                    <Card className="surface-cream mb-20 border-border shadow-sm">
                        <CardContent className="p-8">
                            <div className="flex flex-col items-center gap-8 md:flex-row">
                                <div className="w-full flex-1">
                                    <Skeleton className="mb-4 h-10 w-3/4" />
                                    <Skeleton className="mb-2 h-4 w-full" />
                                    <Skeleton className="mb-2 h-4 w-full" />
                                    <Skeleton className="mb-8 h-4 w-full" />
                                    <div className="flex flex-col gap-4 sm:flex-row">
                                        <Skeleton className="h-12 w-full rounded-full sm:w-1/3" />
                                        <Skeleton className="h-12 w-full rounded-full sm:w-1/3" />
                                    </div>
                                </div>
                                <div className="flex w-full flex-1 justify-center">
                                    <Skeleton className="h-64 w-64 rounded-[24px]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* FAQ skeleton */}
                    <div className="mb-20">
                        <Skeleton className="mx-auto mb-10 h-10 w-1/2" />
                        <div className="grid gap-6 md:grid-cols-2">
                            {[...Array(4)].map((_, index) => (
                                <Card key={index} className="border-border shadow-sm">
                                    <CardContent className="pb-6 pt-6">
                                        <Skeleton className="mb-2 h-6 w-3/4" />
                                        <Skeleton className="mb-2 h-4 w-full" />
                                        <Skeleton className="h-4 w-full" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </main>

                {/* Footer skeleton */}
                <footer className="mt-20 pb-8 text-center">
                    <div className="mb-6 flex justify-center space-x-6">
                        <Skeleton className="h-6 w-16" />
                        <Skeleton className="h-6 w-16" />
                        <Skeleton className="h-6 w-16" />
                    </div>
                    <Skeleton className="mx-auto h-6 w-48" />
                </footer>
            </div>
        </div>
    );
}
